import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { IconArrowLeft, IconUser } from '@tabler/icons-react';

export default function StaffProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();
  const [staff, setStaff] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedOrgId = orgId || userProfile?.orgId;
  const branchId = userProfile?.branchId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const userDoc = await getDoc(
          doc(db, 'organisations', resolvedOrgId, 'users', userId)
        );
        if (userDoc.exists()) {
          setStaff({ id: userDoc.id, ...userDoc.data() });

          // Load recent checks by this staff member
          const staffBranchId = userDoc.data().branchId || branchId;
          if (staffBranchId) {
            try {
              const checksSnap = await getDocs(
                query(
                  collection(db, 'organisations', resolvedOrgId, 'branches', staffBranchId, 'checkRecords'),
                  where('submittedBy', '==', userId),
                  orderBy('submittedAt', 'desc'),
                  limit(5)
                )
              );
              setChecks(checksSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
              console.error('Error loading checks:', err);
            }
          }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [userId, resolvedOrgId, branchId]);

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const roleColours = {
    operator: { bg: '#EAF3DE', color: '#3B6D11' },
    driver: { bg: '#EEEDFE', color: '#534AB7' },
    manager: { bg: '#E6F1FB', color: '#185FA5' },
  };

  if (loading) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
    </Layout>
  );

  if (!staff) return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/staff')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Staff
        </div>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)' }}>Staff member not found</div>
      </div>
    </Layout>
  );

  const roleColour = roleColours[staff.role] || { bg: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' };
  const branchName = staff.branchId?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '—';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div onClick={() => navigate('/staff')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 16 }}>
          <IconArrowLeft size={15} stroke={1.5} /> Staff
        </div>

        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#185FA5', flexShrink: 0 }}>
            {getInitials(staff.name)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 400, marginBottom: 6 }}>{staff.name || staff.email}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {staff.role && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: roleColour.bg, color: roleColour.color, textTransform: 'capitalize' }}>
                  {staff.role}
                </span>
              )}
              {(staff.branchId || staff.departmentId) && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                  {branchName}{staff.departmentId ? ` · ${staff.departmentId}` : ''}
                </span>
              )}
              {!staff.onboardingComplete && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#FDF0E8', color: '#993C1D' }}>
                  Invitation pending
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Contact details */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Contact details</div>
              {[
                { label: 'Email', value: staff.email },
                { label: 'Phone', value: staff.phone },
                { label: 'Job title', value: staff.jobTitle },
              ].filter(r => r.value).map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.label}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.value}</span>
                </div>
              ))}
            </div>

            {/* Licence details */}
            {staff.licence && (
              <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Licence details</div>
                {[
                  { label: 'Licence number', value: staff.licence.number },
                  { label: 'Expiry', value: staff.licence.expiry },
                  { label: 'Categories', value: staff.licence.categories?.join(', ') },
                  { label: 'Endorsements', value: staff.licence.endorsements > 0 ? `${staff.licence.endorsements} endorsement` : 'None' },
                ].filter(r => r.value).map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.label}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{r.value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Assignment */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Assignment</div>
              {[
                { label: 'Branch', value: branchName },
                { label: 'Department', value: staff.departmentId || '—' },
                { label: 'Assigned asset', value: staff.assignedAssetReg || 'Unassigned' },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid var(--color-border-light)' }}>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{r.label}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>
                    {r.label === 'Assigned asset' && staff.assignedAssetReg
                      ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{staff.assignedAssetReg}</span>
                      : r.value
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent checks */}
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Recent checks</div>
            {checks.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <IconUser size={28} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>No checks recorded yet</div>
              </div>
            ) : checks.map((c, i) => (
              <div
                key={c.id}
                onClick={() => navigate(`/checks/${c.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < checks.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{c.assetName || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    {c.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 4px', borderRadius: 3 }}>{c.assetReg}</span>}
                    {formatTime(c.submittedAt)}
                  </div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: c.result === 'pass' ? 'var(--color-success-light)' : c.result === 'fail' ? 'var(--color-danger-light)' : 'var(--color-warning-light)', color: c.result === 'pass' ? 'var(--color-success)' : c.result === 'fail' ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                  {c.result === 'pass' ? 'Pass' : c.result === 'fail' ? 'Fail' : 'Issues'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}